import { MatDialog } from '@angular/material/dialog';
import { PolicyDataService } from '@modules/policy/services/shared/policy-data.service';
import { RatingService } from '../../../services/rating/rating.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';

import { Rating, BindResult } from '@shared/models/rating.model';
import { BasicDialogComponent } from '@modules/policy/shared/components/basic-dialog/basic-dialog.component';
import { UnderwriterReviewDialogComponent } from '@modules/policy/shared/components/underwriter-review-dialog/underwriter-review-dialog.component';
import { Error } from '@shared/models/error.model';
import { AssetFetcherService } from '@modules/policy/services/shared/asset-fetcher.service';
import { PdfGeneratorService } from '@modules/policy/services/pdf/pdf-generator.service';

@Component({
  selector: 'app-rating',
  templateUrl: './rating.component.html',
  styleUrls: ['./rating.component.scss'],
})
export class RatingComponent implements OnInit {
  errorPayload: Error;
  moreDetails: boolean = false;
  needsUnderwriterReview: boolean = false;
  checkoutHidden: boolean;
  canDownloadPDF: boolean;
  generalCoverageColumns: string[] = ['Name', 'Limit', 'Deductible', 'Premium'];
  locationCoverageColumns: string[] = ['Name', 'Limit', 'Deductible', 'BasisofSettlement', 'Premium'];
  discountsAndSurchargeColumns: string[] = ['Name', 'Amount', 'Percent'];

  data: any;
  policyId: string;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private RatingService: RatingService,
    private dialog: MatDialog,
    private PolicyDataService: PolicyDataService,
    private assetFetcherService: AssetFetcherService,
    private pdfService: PdfGeneratorService
  ) { }

  get canFinalizeQuote() {
    return Boolean(this.data?.UserAction?.workflows?.includes('document-upload'))
  }

  ngOnInit() {
    this.policyId = this.PolicyDataService.getPolicyID();
    this.rate();
  }

  rate() {
    this.RatingService.ratePolicy(this.policyId).subscribe(
      (data) => {
        this.PolicyDataService.updateUnderWritingRules(data.opinions);
        if (data.Error) {
          this.errorPayload = data;
          if (data.UserAction != null && data.UserAction.modal === 'underwriter-review-dialog') {
            this.needsUnderwriterReview = true;
          }
          return;
        }
        if (data.UserAction?.workflows.includes('pdf-application')) {
          this.canDownloadPDF = true;
          this.pdfService.generatePdf(data);
        }
        this.checkoutHidden = this.PolicyDataService.getPolicyConfig()?.checkoutHidden
        this.data = data;
      });
  }

  openErrorDialog(error: Error) {
    this.dialog.open(BasicDialogComponent, {
      data: {
        message: error.Message,
        details: error.Details,
        onlyConfirm: true,
        title: 'Error',
        showDetails: (error.Details != null),
        dialogPrefix: 'rating-error-modal'
      },
      width: '480px'
    });
  }

  toggleDetails() {
    this.moreDetails = !this.moreDetails;
  }

  initiateBind() {
    this.assetFetcherService.getAssetFile({
      quoteId: this.PolicyDataService.getPolicyID(),
      name: 'bind-warning-message-B.html'
    }).subscribe(
      (res) => {
        this.openWarningDialog(
          res.data,
          this.bindPolicy.bind(this)
        );
      }
    );
  }

  initiateCheckout() {
    this.assetFetcherService.getAssetFile({
      quoteId: this.PolicyDataService.getPolicyID(),
      name: 'bind-warning-message-D.html'
    }).subscribe(
      (res) => {
        this.openWarningDialog(
          res.data,
          this.checkout.bind(this)
        );
      }
    );
  }

  openWarningDialog(htmlContent: string, proceedMethod: () => void) {
    this.dialog
      .open(BasicDialogComponent, {
        data: {
          title: 'Warning',
          htmlContent,
          dialogPrefix: 'checkout-warning-modal',
        }
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (confirm) {
          proceedMethod();
        }
      });
  }

  openUnderwriterReviewDialog() {
    this.dialog.open(UnderwriterReviewDialogComponent, {
      data: {}
    });
  };

  openPdf() {
    this.pdfService.openPdf();
  }

  downloadQuoteSummary() {
    if (!this.data) return;
    
    this.PolicyDataService.getHeaderData(this.policyId).subscribe(headerData => {
      const quoteNumber = headerData?.PolicyQuoteNumberTxt || this.data["Reference#"] || 'N/A';
      const quoteDescription = headerData?.PolicyDescriptionTxt || this.PolicyDataService.policyDescriptionSubject.getValue() || '';
      
      this.pdfService.generateQuoteSummaryPdf(this.data, quoteNumber, quoteDescription, () => {
        this.pdfService.openQuoteSummaryPdf();
      });
    });
  }
  
  bindPolicy() {
    this.RatingService.bindPolicy(this.policyId).subscribe(
      (bindResult: BindResult) => {
        this.handleBindResult(
          bindResult,
          () => {
            this.dialog
              .open(BasicDialogComponent, {
                data: {
                  title: 'Success',
                  message: `Policy ${bindResult.PolicyISIPolicyKeyID} was successfully bound.`,
                  dialogPrefix: 'checkout-success-modal',
                  onlyConfirm: true,
                },
                width: '480px'
              })
              .afterClosed()
              .subscribe((_: boolean) => {
                this.router.navigate([`/dashboard`]);
              });
          }
        );
      });
  }

  checkout() {
    this.RatingService.checkout(this.policyId).subscribe(
      (bindResult: BindResult) => {
        this.handleBindResult(
          bindResult,
          () => {
            this.router.navigate([`/billing/${this.policyId}`]);
          }
        )
      });
  }

  handleBindResult(bindResult: BindResult, proceedMethod: () => void) {
    const { Error, UserAction } = bindResult;

    if (Error != null) {
      this.openErrorDialog(Error);

    } else if (UserAction != null && UserAction.modal === 'underwriter-review-dialog') {
      this.openUnderwriterReviewDialog()
    } else {
      proceedMethod();
    }
  }

  fileUploadWorkflow() {
    this.router.navigate(['../document-upload'], { state: { navigation: true }, relativeTo: this.route })
  }

  getColumnsFromObj(entityObj) {
    return Object.keys(entityObj);
  }

  getLocationRisks(location) {
    return location.Risks ?? location.Vehicles ?? []
  }

  isAuto(): boolean {
    return this.PolicyDataService.getLOB() === 'A'; //checks policy type
  }

  calculatePremiumFromCoverages(coverages: any[]): number { 
    if (!coverages || !Array.isArray(coverages)) return 0; 
    return coverages.reduce((total, coverage) => { 
      const premium = this.parsePremium(coverage.Premium); 
      return total + premium; 
    }, 0); 
  }

  calculatePremiumFromItems(items: any[]): number {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => {
      let itemTotal = 0;
      if (item.Coverages) {
        itemTotal += this.calculatePremiumFromCoverages(item.Coverages);
      }
      return total + itemTotal;
    }, 0);
  }

  getLocationPremium(location: any): string {
    let total = 0;
     
    if (location.Liability) {
      total += this.calculatePremiumFromItems(Array.isArray(location.Liability) ? location.Liability : [location.Liability]);
    }
    
    //Floaters 
    if (location.Floaters) {
      total += this.calculatePremiumFromItems(Array.isArray(location.Floaters) ? location.Floaters : [location.Floaters]);
    }
    
    //Add premiums from all risks in location
    const risks = this.getLocationRisks(location);
    risks.forEach(risk => {
      if (risk.Coverages) {
        total += this.calculatePremiumFromCoverages(risk.Coverages);
      }
    });
    
    return this.formatPremium(total);
  }

  getRiskPremium(risk: any): string{
    if (!risk.Coverages) return this.formatPremium(0); 
    const total = this.calculatePremiumFromCoverages(risk.Coverages); 
    return this.formatPremium(total); 
  }

  parsePremium(premium: string | number): number {
    if (!premium) return 0;
    if (typeof premium === 'number') return premium; 

    const premium_formatted = premium.toString().replace(/[^0-9.-]/g, ''); 
    const parsed = parseFloat(premium_formatted); 
    return isNaN(parsed) ? 0 : parsed; 
  }

  formatPremium(amount: number): string {
    return new Intl.NumberFormat('en-CA', { 
      style: 'currency', 
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}
